import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { BASE_URL } from "../../../../data/BaseUrl";
import AnalyticsOverviewTab from "./AnalyticsOverviewTab";
import AnalyticsDisciplineLeaderboard from "./AnalyticsDisciplineLeaderboard";
import AnalyticsGymHealthScores from "./AnalyticsGymHealthScores";
import AnalyticsPatternsTab from "./AnalyticsPatternsTab";
import "../../../../styles/trainer-report/analytics-tab.css";

const MAX_FETCH_LIMIT = 9999;
const FETCH_CONCURRENCY = 5; // max parallel /staff/report/v3 requests

/**
 * Minimal concurrency limiter — no extra dependency needed.
 * Returns a wrapped version of `fn` that queues when `limit` slots are full.
 */
const makeLimiter = (limit) => {
  let active = 0;
  const queue = [];
  const next = () => {
    if (active >= limit || queue.length === 0) return;
    active++;
    const { fn, resolve, reject } = queue.shift();
    fn().then(resolve, reject).finally(() => { active--; next(); });
  };
  return (fn) => new Promise((resolve, reject) => { queue.push({ fn, resolve, reject }); next(); });
};

const ATABS = {
  OVERVIEW:   "overview",
  DISCIPLINE: "discipline",
  GYM_HEALTH: "gym_health",
  PATTERNS:   "patterns",
};

const ATAB_LABELS = {
  [ATABS.OVERVIEW]:   "Overview",
  [ATABS.DISCIPLINE]: "Staff Discipline",
  [ATABS.GYM_HEALTH]: "Gym Health",
  [ATABS.PATTERNS]:   "Patterns",
};

export const isLate = (att) =>
  att?.status === "Late" || att?.status_value === "late";

export const isAbsent = (att) => {
  const cs = att?.checkin_status?.toLowerCase?.() ?? "";
  const sv = att?.status_value?.toLowerCase?.() ?? "";
  return cs === "absent" || sv === "absent";
};

export const isPresent = (att) =>
  att?.checkin_status === "Checked-In" ||
  att?.checkout_status === "checked-out" ||
  att?.status === "Late" ||
  att?.status === "Early" ||
  att?.status === "On Time";

export const parseLateMinutes = (diff) => {
  if (!diff || !diff.includes(":")) return 0;
  const [h, m] = diff.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Returns a list of "YYYY-MM-DD" strings for every calendar day in [from, to].
const buildDateList = (from, to) => {
  const dates = [];
  let cur = moment(from, "YYYY-MM-DD");
  const end = moment(to, "YYYY-MM-DD");
  while (cur.isSameOrBefore(end, "day")) {
    dates.push(cur.format("YYYY-MM-DD"));
    cur.add(1, "day");
  }
  return dates;
};

const flattenShifts = (staffData) => {
  const out = [];
  staffData.forEach((staff) => {
    (staff.shift_details || []).forEach((shift) => {
      // Prefer the per-day query date injected during fetch (_date).
      // Absent staff have attendance_details.date = null (they never checked in),
      // so without _date they would be silently dropped — that was the bug.
      const date =
        staff._date ||
        (shift.attendance_details?.date
          ? moment(shift.attendance_details.date, "DD-MM-YYYY").format("YYYY-MM-DD")
          : null);
      if (!date) return;
      out.push({
        userId:      staff.user_id,
        name:        staff.name || staff.full_name || "—",
        accountType: staff.account_type,
        gymId:       staff.gym_id,
        gymName:     staff.gym_name || "—",
        date,
        att: shift.attendance_details || {},
      });
    });
  });
  return out;
};

const fetchStaffData = async (dateFrom, dateTo, token, gymFilter, signal) => {
  const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, limit: MAX_FETCH_LIMIT, page: 1 });
  if (gymFilter?.value) params.set("gym_id", gymFilter.value);
  const res = await fetch(`${BASE_URL}/staff/report/v3?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.data) ? json.data : Array.isArray(json.result) ? json.result : [];
};

const StaffAnalyticsTab = ({ gymFilter }) => {
  const today       = moment().format("YYYY-MM-DD");
  const defaultFrom = moment().subtract(6, "days").format("YYYY-MM-DD"); // 7-day default (6 back + today)

  const [dateFrom,      setDateFrom]      = useState(defaultFrom);
  const [dateTo,        setDateTo]        = useState(today);
  const [activeTab,     setActiveTab]     = useState(ATABS.OVERVIEW);
  const [allShifts,     setAllShifts]     = useState([]);
  const [prevShifts,    setPrevShifts]    = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const token = useSelector((state) => state.auth?.user?.token);

  const prevDateTo = useMemo(
    () => moment(dateFrom, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD"),
    [dateFrom]
  );
  const prevDateFrom = useMemo(() => {
    const days = moment(dateTo, "YYYY-MM-DD").diff(moment(dateFrom, "YYYY-MM-DD"), "days");
    return moment(prevDateTo, "YYYY-MM-DD").subtract(days, "days").format("YYYY-MM-DD");
  }, [dateFrom, dateTo, prevDateTo]);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // The backend /staff/report/v3 is a single-day API — it uses date_from for all
        // time calculations and sets attendance_details.date = null for absent staff.
        // Fetching a multi-day range collapses 30 days into one record per staff member
        // and loses per-day absent records. Fix: one request per calendar day, then
        // annotate each record with _date so flattenShifts can use it for absent staff.
        const currDates = buildDateList(dateFrom, dateTo);
        const prevDates = buildDateList(prevDateFrom, prevDateTo);
        // Shared concurrency limiter: curr + prev together stay within FETCH_CONCURRENCY.
        // Default 7-day range = 14 requests max; user-extended ranges are also capped.
        const limit = makeLimiter(FETCH_CONCURRENCY);
        const [currByDay, prevByDay] = await Promise.all([
          Promise.all(currDates.map((d) => limit(() => fetchStaffData(d, d, token, gymFilter, controller.signal)))),
          Promise.all(prevDates.map((d) => limit(() => fetchStaffData(d, d, token, gymFilter, controller.signal)))),
        ]);
        const annotate = (byDay, dates) =>
          byDay.flatMap((records, i) => records.map((r) => ({ ...r, _date: dates[i] })));
        setAllShifts(flattenShifts(annotate(currByDay, currDates)));
        setPrevShifts(flattenShifts(annotate(prevByDay, prevDates)));
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to load analytics data. Please try again.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [dateFrom, dateTo, token, reloadTrigger, gymFilter, prevDateFrom, prevDateTo]);

  if (loading) {
    return (
      <div className="sta-container">
        <div className="sta-loading">Loading analytics…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sta-container">
        <div className="sta-error">
          {error}
          <button type="button" className="sta-error__retry" onClick={() => setReloadTrigger((n) => n + 1)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sta-container">
      <div className="sta-page-header">
        <span className="sta-page-header__title">Staff Analytics</span>
        <span className="sta-controls__label">From</span>
        <input type="date" className="sta-controls__picker" value={dateFrom} max={dateTo}
          onChange={(e) => e.target.value && setDateFrom(e.target.value)} />
        <span className="sta-controls__label">To</span>
        <input type="date" className="sta-controls__picker" value={dateTo} max={today}
          onChange={(e) => e.target.value && setDateTo(e.target.value)} />
        <button type="button" className="sta-controls__reload" onClick={() => setReloadTrigger((n) => n + 1)}>
          ↻ Reload
        </button>
      </div>

      <div className="sta-tab-header" role="tablist">
        {Object.entries(ATAB_LABELS).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            className={`sta-tab-btn${activeTab === key ? " sta-tab-btn--active" : ""}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {allShifts.length === 0 ? (
        <div className="sta-empty">No data found for this date range.</div>
      ) : (
        <>
          {activeTab === ATABS.OVERVIEW   && <AnalyticsOverviewTab allShifts={allShifts} prevShifts={prevShifts} dateFrom={dateFrom} dateTo={dateTo} />}
          {activeTab === ATABS.DISCIPLINE && <AnalyticsDisciplineLeaderboard allShifts={allShifts} prevShifts={prevShifts} dateFrom={dateFrom} dateTo={dateTo} />}
          {activeTab === ATABS.GYM_HEALTH && <AnalyticsGymHealthScores allShifts={allShifts} prevShifts={prevShifts} />}
          {activeTab === ATABS.PATTERNS   && <AnalyticsPatternsTab allShifts={allShifts} dateFrom={dateFrom} dateTo={dateTo} />}
        </>
      )}
    </div>
  );
};

export default StaffAnalyticsTab;
